import { theme as antdTheme } from "antd";

// ---------------------------------------------------------------------------
// Theme is now mode-aware. `buildTheme(mode)` returns the antd ConfigProvider
// theme for "light" or "dark". LIGHT VALUES ARE UNCHANGED from the previous
// static theme — they are preserved exactly so the existing UI is untouched.
//
// How dark mode is derived:
//  - Standard antd tokens (Table.colorText, Modal.titleColor, Button.defaultBg,
//    borders, etc.) are NOT hand-set in dark; they are OMITTED so antd's
//    `darkAlgorithm` generates a coherent dark value. They keep their exact
//    light literal in light mode.
//  - Custom tokens the algorithm cannot see (whiteIcon, baseColorText,
//    contentBg, saitHeaderBorder, ...) get an explicit dark value where the
//    light value would be invisible/wrong on a dark surface.
//  - Brand greens, white-on-dark text/icons, map markers and the existing navy
//    chrome (#001529 sider/header/tabs) are identical in both modes by design:
//    the chrome stays constant across modes, only the content surface flips.
// ---------------------------------------------------------------------------

export const buildTheme = (mode = "light") => {
  const dark = mode === "dark";

  return Object.freeze({
    algorithm: dark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
    token: {
      fontFamily: `-apple-system, BlinkMacSystemFont, 'SF Pro Text', 'Segoe UI', Roboto, 'Helvetica Neue', Arial,
    'Noto Sans', sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol',
    'Noto Color Emoji'`,
      // please read this comment !!!!!!!!! if i am not mystaken this can help us for icons that we dont have control forexzample input=search
      colorPrimary: "#61BC18",
      colorSecondaryLogo: "rgba(173,62,240,255)",
    },
    components: {
      Layout: {
        algorithm: true, // Enable algorithm
        colorBgLayout: "#001529", // navy chrome — same in both modes
        // Main content surface. Light unchanged (#FFF); dark = neutral #141414.
        contentBg: dark ? "#141414" : "#FFF",
      },
      Header: {
        // Header accent border. Light unchanged (#FFF); dark = subtle divider.
        saitHeaderBorder: dark ? "rgba(255, 255, 255, 0.15)" : "#FFF",
        // 3px inset line on the header's left edge. It blends into the content
        // surface: white on the light #FFF content, #141414 on the dark content
        // (so it stays invisible in both, as designed).
        boxShadow: dark
          ? " 3px 0px 0px 0px #141414 inset"
          : " 3px 0px 0px 0px #FFF inset",
      },
      Button: {
        algorithm: true, // Enable algorithm
        colorPrimary: "#61BC18",
        // defaultBg / defaultBorderColor are standard tokens: keep the exact
        // light literals in light, let darkAlgorithm produce the dark values.
        // In dark mode, drop the primary/danger button shadow: darkAlgorithm
        // brightens it to a vivid green 2px line that shows under buttons
        // (e.g. the custom-red Delete button). Light mode keeps its subtle shadow.
        ...(dark
          ? { primaryShadow: "none", dangerShadow: "none" }
          : { defaultBg: "#FFF", defaultBorderColor: "#d9d9d9" }),
        fontWeight: 400,
        // colorText: "#FFF",
        controlHeight: 40,
        fontSize: 16,
        colorLink: "#61BC18",
      },
      Menu: {
        algorithm: true,
        // Generic (light) Menu tokens. In antd v6 these are ALSO applied to
        // Dropdown popups (Dropdown renders a Menu internally), so they must
        // stay light-appropriate: white bg, dark text. The dark Sider menu
        // uses `theme="dark"` + the explicit `darkItem*` tokens below.
        itemSelectedColor: "#FFFFFF",
        itemSelectedBg: "#61BC18",
        itemHoverBg: "#61BC18",
        itemHoverColor: "#FFFFFF",
        itemPaddingInline: "24px",
        // antd dark-Menu token names — required to render the Sider's dark
        // Menu (`theme="dark"`) with our brand colors.
        darkItemBg: "#001529",
        darkSubMenuItemBg: "#000C17",
        darkItemColor: "#FFFFFFA6",
        darkItemHoverBg: "#61BC18",
        darkItemHoverColor: "#FFFFFF",
        darkItemSelectedBg: "#61BC18",
        darkItemSelectedColor: "#FFFFFF",
      },
      Tabs: {
        cardBg: "#001529", // navy tab bar — chrome, same in both modes
        itemColor: "#FFF",
        colorBorderSecondary: "transparent",
        cardGutter: 0,
        colorBorder: "transparent",
        horizontalMargin: "0",
        fontSizeLG: 16,
      },
      Segmented: {
        // Standard tokens — light literals kept, dark left to the algorithm.
        ...(dark
          ? {}
          : {
              colorText: "#000",
              colorBgElevated: "#FFF", //this is for only one item backGroundCOlor
              colorBgLayout: "rgba(0, 0, 0, 0.04)",
            }),
        controlPaddingHorizontal: 13,
        controlHeight: 40,
      },
      // Select: {
      //   // controlHeight: 40,
      // },
      Input: {
        hoverBorderColor: "#61BC18",
        // colorBorder is standard: light literal kept, dark via algorithm.
        ...(dark ? {} : { colorBorder: "#d9d9d9" }),
        activeBorderColor: "#61BC18",
      },
      Table: {
        cellPaddingInline: 8,
        cellPaddingBlock: 8,
        // colorText / iconWrapperBorderColor / headerBg are standard tokens:
        // light literals kept, dark generated by darkAlgorithm.
        ...(dark
          ? {}
          : {
              colorText: "#000",
              iconWrapperBorderColor: "#d9d9d9",
              headerBg: "#fafafa",
            }),
      },
      Modal: {
        boxShadow:
          "0px 9px 28px 8px rgba(0, 0, 0, 0.05), 0px 3px 6px -4px rgba(0, 0, 0, 0.12), 0px 6px 16px 0px rgba(0, 0, 0, 0.08)",
        titleFontSize: 16,
        // titleColor / colorPrimaryBorder standard: light kept, dark via algorithm.
        ...(dark
          ? {}
          : { titleColor: "#000", colorPrimaryBorder: "rgba(0, 0, 0, 0.15)" }),
        // (removed dead token `trasparentBg` — 0 usages)
      },
      Typography: {
        // colorText standard: light literal kept, dark via algorithm.
        ...(dark ? {} : { colorText: "#000000E0" }),
        whieteColorText: "#FFF", // white text on dark surfaces — same both modes
        baseColorText: "#61BC18", // brand green — same both modes
        grayColorText: "rgba(255, 255, 255, 0.60)",
        secondaryColorText: "rgba(173,62,240,255)",
      },
      icons: {
        // it seems me that this is comfortable option for cntroling icons color from one place
        whiteIcon: "#FFF",
        baseColor: "#61BC18",
        // userIcon: semi-transparent black is invisible on dark → mirror to white.
        userIcon: dark ? "rgba(255, 255, 255, 0.45)" : "#00000073",
        // (removed dead token `erorrColorIcon` — 0 usages)
        grayIcon: "#ffffff99",
        // colorPlaceholder: black placeholder invisible on dark → mirror to white.
        colorPlaceholder: dark ? "rgba(255, 255, 255, 0.25)" : "rgba(0,0,0,.25)",
      },
      Card: {
        colorPrimary: "#61BC18",
        boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.08)",
        paddingLG: 12,
        headerHeight: 46,
      },
      Form: {
        itemMarginBottom: 0,
      },
      Dropdown: {
        algorithm: true,
        baseColorText: "#1677FF",
      },
      Collapse: {
        borderTopColor: "#61BC18",
        boxShadow: " 0px 0px 8px 0px rgba(0, 0, 0, 0.12)",
      },
      BLP: {
        colorText: "#FFFFFF",
        colorBg: "#424242",
      },
      Map: {
        colorText: "#FFFFFF",
        redColorBg: "#cc0000",
        newColorBg: "#AD3EF0",
        driverAssigned: "#00cae9",
      },
      // Radio: {
      //     dotSize: 24,
      //     radioSize: 24,
      //     buttonBg: '#1677FF',
      //     buttonCheckedBg: '#ff4d4f',
      //     buttonColor: 'red',
      // }
    },
  });
};

// Backwards-compatible default light theme — existing imports keep working.
export const theme = buildTheme("light");

export const invertTheme = Object.freeze({
  token: {
    colorPrimary: "rgba(255, 255, 255, 0.60)",
    colorBgContainer: "#001529",
    colorText: "#FF0000",
    colorTextPlaceholder: "rgba(255, 255, 255, 0.60)",
    colorText: "rgba(255, 255, 255, 0.60)",
    colorBgElevated: "#001529",

    colorPrimary: "#4d3232",
    colorInfo: "#4d3232",
    colorLink: "#4230b1",
    colorBgBase: "#404f93",
    wireframe: false,
    colorTextBase: "#f50505",
  },
  components: {
    Select: {},
    TimePicker: {},
  },
});

// if you see baseColor it means taht the color of that property will be th same as menu's selected state color and etc.
